/**
 * Tab resolution for the organization detail page.
 *
 * Kept out of the page component so the URL contract is testable on its own:
 * admin-ui has no interactive test renderer (no @testing-library/react), so a
 * rule left inline in JSX can only be verified by clicking it.
 *
 * The active tab lives in the query string (`?tab=details|members|rooms`) — the same
 * contract as CustomersPage — so a refresh, a deep link or a browser back
 * restores the tab the operator was on (house rule: restore selection state
 * from the URL). An unknown or missing value falls back to Details rather than
 * rendering nothing.
 */

export type OrgDetailTab = 'details' | 'members' | 'rooms';

export const ORG_DETAIL_TAB_PARAM = 'tab';

export const DEFAULT_ORG_DETAIL_TAB: OrgDetailTab = 'details';

export const isOrgDetailTab = (value: string | null): value is OrgDetailTab =>
    value === 'details' || value === 'members' || value === 'rooms';

export const resolveOrgDetailTab = (value: string | null): OrgDetailTab =>
    (isOrgDetailTab(value) ? value : DEFAULT_ORG_DETAIL_TAB);

/**
 * The next query string for a tab switch, derived from the current one so any
 * other param on the URL survives the switch instead of being dropped.
 */
export const withOrgDetailTab = (
    current: URLSearchParams,
    tab: OrgDetailTab,
): URLSearchParams => {
    const next = new URLSearchParams(current);
    next.set(ORG_DETAIL_TAB_PARAM, tab);
    return next;
};
